import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppNotification,
  NotificationType,
  getActorDisplayName,
  getNotificationLabel,
  getActorProfileUrl,
  getSystemMessageBody,
} from './types';
import ProfileImage from '../account/ProfileImage';
import VerifiedBadge from '../account/badges/VerifiedBadge';
import { useUser } from '../login/UserContext';
import { getFollowStats, followUser } from '../../services/apiService';
import styles from './notifications.module.scss';

interface NotificationItemProps {
  notification: AppNotification;
  /** Keep the unread marker on a row already marked read while the panel is open. */
  highlight?: boolean;
  onClick: (notification: AppNotification) => void;
  onClose: () => void;
}

/** Relative time helper (e.g. "2 mins ago", "3 hrs ago") */
function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const seconds = Math.floor(diffMs / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes === 1) return '1 min ago';
  if (minutes < 60) return `${minutes} mins ago`;
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return '1 hr ago';
  if (hours < 24) return `${hours} hrs ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return '1 week ago';
  if (weeks < 5) return `${weeks} weeks ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return '1 month ago';
  if (months < 12) return `${months} months ago`;
  const years = Math.floor(days / 365);
  if (years === 1) return '1 year ago';
  return `${years} years ago`;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, highlight, onClick, onClose }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { actor, type, message, created_at } = notification;
  const read = notification.read && !highlight;
  const displayName = getActorDisplayName(actor);
  const label = getNotificationLabel(type, message);
  const actorProfileUrl = getActorProfileUrl(actor);

  // Follow-back state (only used for FOLLOW notifications)
  const [alreadyFollowing, setAlreadyFollowing] = useState<boolean | null>(null);
  const [followLoading, setFollowLoading] = useState(false);
  const [followedBack, setFollowedBack] = useState(false);
  const checkedRef = useRef(false);

  const isAuthenticated = user.id > 0;
  const isFollowNotification = type === NotificationType.FOLLOW;
  const isOwnAction = actor.id === user.id;
  // FOLLOW_BACK means the actor followed *us back* — so we already follow them.
  // No need to query, no Follow Back button.
  const isFollowBack = isFollowNotification && message === 'FOLLOW_BACK';

  // Check if the current user already follows the actor (only for FOLLOW notifications
  // that aren't the FOLLOW_BACK variant — those imply we already follow them).
  useEffect(() => {
    if (
      !isFollowNotification ||
      isFollowBack ||
      !isAuthenticated ||
      isOwnAction ||
      checkedRef.current
    ) {
      return;
    }
    checkedRef.current = true;

    const checkFollowStatus = async () => {
      try {
        const stats = await getFollowStats(actor.id);
        setAlreadyFollowing(stats.is_following);
      } catch (err) {
        // Could be 401 (auth expired), network error, etc. Hide the button
        // rather than defaulting to "not following".
        console.warn('[NotificationItem] Skipping Follow Back — follow status unknown:', err);
        setAlreadyFollowing(null);
      }
    };

    checkFollowStatus();
  }, [isFollowNotification, isFollowBack, isAuthenticated, isOwnAction, actor.id]);

  /** Handle the Follow Back action */
  const handleFollowBack = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger the notification row click
    setFollowLoading(true);
    try {
      const success = await followUser(actor.id);
      if (success) {
        setFollowedBack(true);
        setAlreadyFollowing(true);
      }
    } catch (err) {
      console.error('[NotificationItem] Follow back failed:', err);
    } finally {
      setFollowLoading(false);
    }
  };

  // Show Follow Back only when we're certain we don't already follow them.
  // Skips FOLLOW_BACK (already following), unauth'd state, own actions,
  // and the unknown / pending state (alreadyFollowing === null).
  const showFollowBack =
    isFollowNotification &&
    !isFollowBack &&
    isAuthenticated &&
    !isOwnAction &&
    alreadyFollowing === false &&
    !followedBack;

  // For CHALLENGE_RESULT, use a trophy icon instead of actor avatar
  const isChallengeResult = type === NotificationType.CHALLENGE_RESULT;
  // SYSTEM notifications render the message body as the payload and
  // suppress the linkable-actor prefix / Follow Back affordance.
  const isSystem = type === NotificationType.SYSTEM;
  const systemBody = isSystem ? getSystemMessageBody(message) : '';

  // Mailto target for the Contact CTA on SYSTEM notices. Centralized so a
  // future inbox rename only changes one line.
  const SYSTEM_CONTACT_EMAIL = 'notifications@bookiebrawler.com';
  const systemContactHref =
    `mailto:${SYSTEM_CONTACT_EMAIL}` +
    `?subject=${encodeURIComponent('Hey from BookieBrawler')}`;

  /** Open the user's mail client without also firing the row's mark-as-read click. */
  const handleContactClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const avatarUrl =
    actor.picture && actor.picture !== 'null'
      ? actor.picture
      : 'https://storage.googleapis.com/assestsbookiebrawler/icons/profile-default.png';

  /** Navigate to the actor's profile and close the panel */
  const handleActorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (actorProfileUrl) {
      navigate(actorProfileUrl);
    }
    onClose();
  };

  return (
    <div
      className={`${styles.item} ${!read ? styles.itemUnread : ''}`}
      data-notif-id={notification.id}
      data-notif-unread={!notification.read}
      onClick={() => onClick(notification)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick(notification)}
    >
      <div className={styles.itemAvatar}>
        {isChallengeResult ? (
          <span className={styles.trophyIcon} aria-hidden="true">🏆</span>
        ) : (
          <ProfileImage imageUrl={avatarUrl} size={40} />
        )}
      </div>

      {/* Middle column — text wraps freely; timestamp is inline at the end
          (Instagram-style) so a row stays one compact block even with a
          right-side button. */}
      <div className={styles.itemContent}>
        {isSystem ? (
          <>
            <p className={styles.itemText}>
              <span className={styles.itemActorName}>{displayName}</span>
              <span className={styles.systemBadge}>Notice</span>{' '}
              <span className={styles.itemTime}>{timeAgo(created_at)}</span>
            </p>
            {/* Delivery-system notifications carry a headline; legacy admin
                notices are body-only, so the title line simply drops out. */}
            {notification.title ? (
              <p className={styles.systemTitle}>{notification.title}</p>
            ) : null}
            <p className={styles.systemBody}>{systemBody}</p>
          </>
        ) : (
          <p className={styles.itemText}>
            {isChallengeResult ? (
              <span className={styles.itemAction}>{label}</span>
            ) : (
              <>
                {actorProfileUrl ? (
                  <span
                    className={`${styles.itemActorName} ${styles.itemActorLink}`}
                    onClick={handleActorClick}
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleActorClick(e as any); } }}
                  >
                    {displayName}
                  </span>
                ) : (
                  <span className={styles.itemActorName}>{displayName}</span>
                )}
                <VerifiedBadge affiliated={actor.affiliated} size="small" />{' '}
                <span className={styles.itemAction}>{label}</span>
              </>
            )}{' '}
            <span className={styles.itemTime}>{timeAgo(created_at)}</span>
          </p>
        )}
      </div>

      {/* Right column — a single compact, uniformly-sized action (Instagram's
          "Message"/"Confirm" slot). Only one of these ever shows per row, so
          the trailing edge stays aligned and the text never runs under it. */}
      <div className={styles.itemTrailing}>
        {showFollowBack ? (
          <button
            className={`${styles.itemBtn} ${styles.itemBtnPrimary}`}
            onClick={handleFollowBack}
            disabled={followLoading}
            title="Follow back"
          >
            {followLoading ? 'Following…' : 'Follow Back'}
          </button>
        ) : followedBack ? (
          <span className={styles.itemBtnFollowed}>Following</span>
        ) : isSystem ? (
          <a
            href={systemContactHref}
            onClick={handleContactClick}
            className={`${styles.itemBtn} ${styles.itemBtnPrimary}`}
            aria-label={`Contact ${SYSTEM_CONTACT_EMAIL}`}
          >
            Contact
          </a>
        ) : (
          !read && <div className={styles.unreadDot} />
        )}
      </div>
    </div>
  );
};

export default NotificationItem;
